import { memo, type MouseEvent, useRef } from 'react';
import styled from 'styled-components';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { LinkWithPrefetch } from '@frameio/shared/routing/components/LinkWithPrefetch';
import { consumeEvent } from '@frameio/utils';
import { ListItemContent, Text, theme } from '@frameio/vapor';
import { useOpenWorkspaceProjectMenu } from '../../../../hooks';
import { useProjectNavigationInfoFragment } from '../../gql/generated/ProjectNavigationInfo';
import { ContextMenuButton } from './ContextMenuButton';

type ProjectItemProps = {
  projectId: string;
};

export const ProjectItem = memo(({ projectId }: ProjectItemProps) => {
  const { data } = useProjectNavigationInfoFragment({
    from: {
      __typename: 'Project',
      id: projectId,
    },
  });
  const projectName = data?.name ?? '';
  const isCurrent = useActiveProjectId() === projectId;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const openContextMenu = useOpenWorkspaceProjectMenu();
  const handleContextMenu = (event?: MouseEvent) => {
    if (event) {
      consumeEvent(event);
    }

    openContextMenu({ id: projectId, type: 'project' }, containerRef, event);
  };

  return (
    <ProjectLinkContainer
      ref={containerRef}
      $isCurrent={isCurrent}
      onContextMenu={handleContextMenu}
    >
      <ProjectLink href={getLink({ page: 'project', projectId })}>
        <StyledListItemContent
          label={
            <Text
              enableTooltip={true}
              fontWeight="medium"
              truncate={true}
              variant="body"
            >
              {projectName}
            </Text>
          }
        />
        <ContextMenuButton
          onContextMenu={handleContextMenu}
          projectId={projectId}
        />
      </ProjectLink>
    </ProjectLinkContainer>
  );
});

ProjectItem.displayName = 'ProjectItem';

const ProjectLinkContainer = styled.div<{ $isCurrent: boolean }>`
  background-color: ${(p) =>
    p.$isCurrent ? theme.colors.background.interactive.activated : undefined};
  border-radius: ${theme.radii[6]};
  transition: background-color ${theme.transitions.durations[200]}
    ${theme.transitions.eases.primary};

  &:hover {
    background-color: ${theme.colors.background.interactive.hover};
  }
`;

const ProjectLink = styled(LinkWithPrefetch)`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: ${theme.spaces[4]} ${theme.spaces[8]} ${theme.spaces[4]} 0;
  width: 100%;
`;

const StyledListItemContent = styled(ListItemContent)`
  flex: 1;
  min-width: 0;
  pointer-events: none;
`;
